import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const InitGsap = () => {
  const body = document.body;
  // ===== 1) 세트 단위 준비(경계 gap 제거용) =====
  function prepareLoop(track, { itemGap = '2.5rem' } = {}) {
    track.style.gap = '0';
    track.style.display = 'flex';
    track.style.willChange = 'transform';
    track.style.whiteSpace = 'normal';

    const origChildren = Array.from(track.children);
    const set = document.createElement('div');
    set.className = 'marquee-set';
    set.style.display = 'flex';

    origChildren.forEach((el) => {
      el.style.marginRight = itemGap;
      set.appendChild(el);
    });

    track.appendChild(set);
    track.appendChild(set.cloneNode(true)); // 총 2세트

    const cycleWidth = set.getBoundingClientRect().width;
    return { cycleWidth, set };
  }

  // ===== 2) 메인 =====
  function marquee() {
    const rows = gsap.utils.toArray('.marquee-row');

    // 전역 스크롤 속도 추적(가속용)
    let scrollV = 0;
    ScrollTrigger.create({
      trigger: document.documentElement,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate(self) {
        scrollV = self.getVelocity();
      },
    });

    rows.forEach((row) => {
      const track = row.querySelector('.marquee-track');

      const base = parseFloat(row.dataset.base || '30'); // px/s
      const accel = parseFloat(row.dataset.accel || '0.5'); // 가속 배수
      const gap = row.dataset.gap || '2.5rem';

      let { cycleWidth } = prepareLoop(track, { itemGap: gap });
      let wrapX = gsap.utils.wrap(-cycleWidth, 0);

      let x = 0;
      let svFilt = 0;

      const tick = () => {
        const dt = gsap.ticker.deltaRatio(60);
        svFilt = svFilt * 0.85 + scrollV * 0.15;
        const v = base + svFilt * accel;
        x += v * (dt / 60);
        const xWrapped = wrapX(x);
        gsap.set(track, { x: xWrapped });
      };

      let running = false;
      const start = () => {
        if (running) return;
        running = true;
        gsap.ticker.add(tick);
      };
      const stop = () => {
        if (!running) return;
        running = false;
        gsap.ticker.remove(tick);
      };

      // ★ 뷰포트 진입/이탈로 start/stop 제어
      ScrollTrigger.create({
        trigger: row,
        start: 'top bottom', // 행의 위가 화면 하단에 닿으면 시작
        end: 'bottom top', // 행의 아래가 화면 상단을 지나면 종료
        onEnter: start,
        onEnterBack: start,
        onLeave: stop,
        onLeaveBack: stop,
      });

      // 리사이즈 시 세트 폭 재계산
      let resizeTimer;
      const onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const setEl = track.querySelector('.marquee-set');
          if (!setEl) return;
          cycleWidth = setEl.getBoundingClientRect().width;
          wrapX = gsap.utils.wrap(-cycleWidth, 0);
        }, 120);
      };
      window.addEventListener('resize', onResize);

      // 탭 비활성화/복귀 시 일시정지
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else ScrollTrigger.refresh(); // 복귀 시 상태 갱신 후 재시작은 트리거가 처리
      });

      // 사용자 설정: 모션 최소화면 멈춤
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        stop();
      }
    });
  }

  function flyHeart() {
    const btn = document.getElementById('fly-heart');
    if (btn == null) return;
    const EMOJIS = [
      '❤️',
      '😀',
      '😍',
      '🥰',
      '👍',
      '🫰',
      '🤟',
      '👌',
      '🚀',
      '🔥',
      '💥',
    ];

    const rand = gsap.utils.random;
    const pickEmoji = rand(EMOJIS, true);

    let emitTimer = null;
    let pointer = { x: 0, y: 0, inside: false };
    const pool = [];
    const MAX_POOL = 10;

    // 버튼 내부 좌표로 변환 (border 포함 기준 → abs 포지셔닝 기준과 일치)
    function setPointer(e) {
      const rect = btn.getBoundingClientRect();
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
      if (clientX == null) return;
      pointer.x = clientX - rect.left;
      pointer.y = clientY - rect.top;
    }

    function spawnAtPointer() {
      if (!pointer.inside) return;

      const el = pool.pop() || document.createElement('div');
      el.className = 'emitter';
      el.textContent = pickEmoji();
      el.style.opacity = '1';
      el.style.fontSize = `${rand(16, 28)}px`;
      el.style.transform = 'none'; // 혹시 남은 transform 초기화
      btn.appendChild(el);

      // 마우스 지점 주변으로 소폭 퍼짐
      const spread = rand(0, 18);
      const angle = rand(0, Math.PI * 2);
      const x0 = pointer.x + Math.cos(angle) * spread;
      const y0 = pointer.y + Math.sin(angle) * spread;

      // ★ 퍼센트 보정 금지: xPercent/yPercent 설정하지 않음
      gsap.set(el, {
        x: x0,
        y: y0,
        scale: rand(0.85, 1.25),
        rotate: rand(-15, 15),
        clearProps: 'left,top',
        // xPercent: 0, yPercent: 0,   // 굳이 필요 없지만, 혹시 남아있다면 0으로 강제 가능
        // clearProps: 'xPercent,yPercent' // 대안: 과거 인라인 스타일 제거
      });

      const driftX = rand(-60, 60);
      const rise = rand(90, 250);
      const dur = rand(1.0, 1.7);

      gsap.to(el, {
        x: x0 + driftX,
        y: y0 - rise,
        opacity: 0,
        rotate: `+=${rand(-90, 90)}`,
        ease: 'power1.out',
        duration: dur,
        onComplete: () => {
          el.remove();
          if (pool.length < MAX_POOL) pool.push(el);
        },
      });
    }

    function startEmit() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (emitTimer || !pointer.inside) return;
      emitTimer = setInterval(spawnAtPointer, 180);
    }

    function stopEmit() {
      if (emitTimer) {
        clearInterval(emitTimer);
        emitTimer = null;
      }
    }

    btn.addEventListener('pointerenter', (e) => {
      pointer.inside = true;
      setPointer(e);
      startEmit();
    });
    btn.addEventListener('pointermove', (e) => setPointer(e));
    btn.addEventListener('pointerleave', () => {
      pointer.inside = false;
      stopEmit();
    });
    btn.addEventListener('pointerdown', (e) => {
      setPointer(e);
      for (let i = 0; i < 10; i++) spawnAtPointer();
    });
  }

  function effVideo() {
    const video = document.querySelector('.video');
    const videoWrapper = document.querySelector('.video--wrapper');
    if (!videoWrapper) return;

    gsap.set(videoWrapper, { opacity: 0, scale: 0.9 });
    gsap.to(videoWrapper, {
      scale: 1,
      opacity: 0.5,
      duration: 1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: video,
        start: 'top 80%',
        end: 'bottom 80%',
        scrub: true,
      },
    });
  }

  function classTrigger() {
    const video = document.querySelector('.video');
    ScrollTrigger.create({
      trigger: video,
      start: 'top 80%',
      end: 'max',
      toggleClass: { targets: body, className: 'active' },
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    marquee();
    flyHeart();
    // effVideo();
    classTrigger();
  });
};

export default InitGsap;
